/*
 * Copyright © 2018, Rezo Zero
 *
 * @file InterviewPage.js
 * @author Adrien Scholaert <adrien@rezo-zero.com>
 */

import Page from './Page'
import {
    TweenLite,
    Power2
} from 'gsap'
import { $window } from '../utils/environment'
import waveformSketch from '../sketchs/WaveformSketch'
import $ from 'jquery'
import BootstrapMedia from '../utils/BootstrapMedia'
import * as Utils from '../utils/Utils'

window.p5.disableFriendlyErrors = true

/**
 * InterviewPage.
 *
 * @extends {AbstractPage}
 * @private
 */
export default class InterviewPage extends Page {
    constructor (router, container, context, type) {
        super(router, container, context, type)

        this.secondSectionActive = false
        this.maxBlur = 40
        this.activeBlur = true
        this.blurPercent = this.maxBlur
        this.waveformSketch = null
        this.scrollToTimeout = null

        // Elements
        this.$splitWrapper = this.$cont.find('#split-wrapper')
        this.$splitContainer = this.$splitWrapper.find('#split-container')
        this.$sections = this.$cont.find('.section')
        this.$firstSection = this.$cont.find('#first-section')
        this.$secondSection = this.$cont.find('#second-section')
        this.$secondSectionBlurredContainer = this.$secondSection.find('.section__content-blur')
        this.$text = this.$cont.find('#splited-text')
        this.$imageCont = this.$cont.find('#images-container')
        this.$mobileTopSection = this.$cont.find('#section__top-mobile')
        this.$mobileTopSectionEls = this.$mobileTopSection.find('.section__name')
        this.currentMobileSectionEl = this.$mobileTopSectionEls[0]

        this.currentSection = this.$sections[0]
        this.textHeight = 0
        this.photosHeight = 0
        this.scrollInitiate = false
        this.timeout = null
        this.blockAutoScroll = false
        this.scrollTo = true
        this.sounds = this.$splitWrapper.data('sounds')
        this.scroll = {
            x: 0,
            y: 0
        }

        this.offset = {
            x: 0,
            y: 0
        }

        // Binded methods
        this.onSectionClick = this.onSectionClick.bind(this)
        this.onScroll = this.onScroll.bind(this)
        this.onMouseMove = this.onMouseMove.bind(this)
        this.onSectionMouseOver = this.onSectionMouseOver.bind(this)
        this.onWaveformScrollTo = this.onWaveformScrollTo.bind(this)
        this.onTitleClick = this.onTitleClick.bind(this)
        this.onSmoothScrollTo = this.onSmoothScrollTo.bind(this)
    }

    init () {
        super.init()
        $window.triggerHandler('background.black')

        this.previousPage = window.previousPage
        window.previousPage = 'interview'

        // Create waveform
        this.waveformSketch = new window.p5(p5 => {
            waveformSketch(p5, this.sounds)
        })
        this.waveformSketch.setAutoScroll(this.blockAutoScroll)

        this.setBlur()
    }

    scrollInitiated () {
        $window.triggerHandler('scroll.updateElements')
        $window.on('scrollTo.RZScroll', this.onSmoothScrollTo)

        this.scrollInitiate = true
        this.textHeight = this.$text.innerHeight()
        this.photosHeight = this.$imageCont.innerHeight()
    }

    initEvents () {
        super.initEvents()
        this.$sections.on('click', this.onSectionClick)
        this.$mobileTopSectionEls.on('click', this.onTitleClick)
        this.$sections.on('mouseenter mouseleave', this.onSectionMouseOver)
        $window.on('mousemove', this.onMouseMove)
        $window.on('Event.onScroll', this.onScroll)
        $window.on('waveform.scrollTo', this.onWaveformScrollTo)
    }

    destroyEvents () {
        super.destroyEvents()
        this.$sections.off('click', this.onSectionClick)
        this.$mobileTopSectionEls.off('click', this.onTitleClick)
        this.$sections.off('mouseenter mouseleave', this.onSectionMouseOver)
        $window.off('mousemove', this.onMouseMove)
        $window.off('Event.onScroll', this.onScroll)
        $window.off('scrollTo.RZScroll', this.onSmoothScrollTo)
        $window.off('waveform.scrollTo', this.onWaveformScrollTo)

        // Destroy waveform sketch
        this.waveformSketch.destroy()

        $window.triggerHandler('bubble.colorInitial')
    }

    onTitleClick (e) {
        const index = $(e.currentTarget).index()
        this.moveToSection(index)
    }

    onBubbleManagerSetup () {
        super.onBubbleManagerSetup()
        this.changeBubbles()
    }

    changeBubbles () {
        $window.triggerHandler('bubble.random')
        $window.triggerHandler('bubble.reduceOpacity')
    }

    onSectionMouseOver (e) {
        if (BootstrapMedia.isMinSM()) {
            let $el = $(e.currentTarget)

            if (!$el || $el.hasClass('active')) return

            let decalX = 100

            if (e.type === 'mouseleave') {
                decalX = 0
            } else if (e.type === 'mouseenter') {
                if (!this.secondSectionActive) {
                    decalX = -100
                }
            }

            this.changeSplitWrapperPos(decalX)
        }
    }

    changeSplitWrapperPos (x = 0, duration = 1) {
        TweenLite.to(this.$splitWrapper, duration, {
            x,
            ease: Power2.easeInOut
        })
    }

    onMouseMove (e) {
        if (BootstrapMedia.isMinSM()) {
            const { clientX } = e
            let percentX = (clientX / window.innerWidth) * 100

            TweenLite.to(this.$splitWrapper, 1.5, {
                x: -percentX,
                ease: Power2.easeOut
            })
        }
    }

    onWaveformScrollTo (e, data) {
        if (!this.blockAutoScroll && !this.locked && this.scrollInitiate) {
            if (this.secondSectionActive) {
                let height = this.photosHeight

                if (Utils.isMobile()) {
                    height = window.innerHeight * 1.5
                }

                $window.triggerHandler('scroll.scrollToPercent', {
                    ...data,
                    containerHeight: height,
                    index: 1
                })
            } else {
                let height = this.textHeight

                if (Utils.isMobile()) {
                    height = window.innerHeight * 1.5
                }

                $window.triggerHandler('scroll.scrollToPercent', {
                    ...data,
                    containerHeight: height,
                    index: 0
                })
            }
        }
    }

    onSmoothScrollTo () {
        this.scrollTo = true
        window.clearTimeout(this.scrollToTimeout)
        this.scrollToTimeout = window.setTimeout(() => {
            this.scrollTo = false
        }, 0)
    }

    onScroll (e) {
        if (e && e.options && e.options.scrollData) {
            const { limit, offset } = e.options.scrollData
            const percentY = (offset.y / limit.y) * 100

            if (!this.secondSectionActive) {
                this.blurPercent = this.maxBlur - (percentY * this.maxBlur) / 100
            }

            if (!this.scrollTo) {
                this.blockAutoScroll = true
                this.waveformSketch.setAutoScroll(this.blockAutoScroll)

                $window.triggerHandler('waveform.scroll', {
                    percentY: percentY
                })

                // Important
                window.clearTimeout(this.timeout)
                this.timeout = window.setTimeout(() => {
                    this.blockAutoScroll = false
                    this.waveformSketch.setAutoScroll(this.blockAutoScroll)
                }, 0) // Important
            }
        }
    }

    setBlur () {
        if (Utils.isMobile()) return
        if (this.activeBlur) {
            TweenLite.to(this.$secondSectionBlurredContainer, 0.3, {
                filter: `blur(${this.blurPercent}px)`
            })

            this.blurTimeout = window.setTimeout(() => {
                this.setBlur()
            }, 1000)
        } else {
            window.clearTimeout(this.blurTimeout)
        }
    }

    resetBlur () {
        if (Utils.isMobile()) return

        TweenLite.to(this.$secondSectionBlurredContainer, 1, {
            filter: `blur(${this.blurPercent}px)`
        })

        this.activeBlur = true
        this.setBlur()
    }

    clearBlur () {
        if (Utils.isMobile()) return

        this.activeBlur = false
        TweenLite.to(this.$secondSectionBlurredContainer, 1, {
            filter: `blur(0px)`
        })
    }

    hideFirstSection () {
        TweenLite.to(this.$firstSection, 1, {
            alpha: 0.5
        })
    }

    showFirstSection () {
        TweenLite.to(this.$firstSection, 1, {
            alpha: 1
        })
    }

    /**
     * @param {Event} e
     */
    onSectionClick (e) {
        this.moveToSection($(e.currentTarget).index())
    }

    moveToSection (index) {
        const el = this.$sections[index]

        if (el === this.currentSection) {
            return false
        }

        if (this.currentSection) {
            this.currentSection.classList.remove('active')
        }

        if (this.currentMobileSectionEl) {
            this.currentMobileSectionEl.classList.remove('active')
        }

        this.currentSection = el
        this.currentMobileSectionEl = this.$mobileTopSectionEls[index]
        this.currentSection.classList.add('active')
        this.currentMobileSectionEl.classList.add('active')

        if (parseInt(this.currentSection.dataset.index) === 2) {
            this.secondSectionActive = true
            this.clearBlur()
            this.hideFirstSection()
            $window.triggerHandler('bubble.colorWhite')
        } else {
            this.secondSectionActive = false
            this.resetBlur()
            this.showFirstSection()
            $window.triggerHandler('bubble.colorInitial')
        }

        this.waveformSketch.scene.changeSound()
        this.moveToCurrentSection()
        this.changeSplitWrapperPos(0)
    }

    moveToCurrentSection () {
        let x = 0
        let xCanvas = 0

        if (this.currentSection.dataset.direction === 'right') {
            if (Utils.isMobile()) {
                x = -window.innerWidth
            } else {
                x = -(window.innerWidth / 10) * 6
            }
        }

        if (Utils.isMobile() && this.waveformSketch.canvas) {
            if (this.currentSection.dataset.direction === 'right') {
                xCanvas = (window.innerWidth * 2) - 90
            }

            TweenLite.to(this.waveformSketch.canvas, 1, {
                x: xCanvas,
                ease: Power2.easeInOut
            })
        }

        TweenLite.to(this.$splitContainer, 1, {
            x,
            ease: Power2.easeInOut
        })
    }

    onResize () {
        super.onResize()
        this.moveToCurrentSection()
    }

    onTransitionComplete () {
        super.onTransitionComplete()
        this.changeBubbles()
        $window.triggerHandler('scroll.updateElements')
    }
}



// WEBPACK FOOTER //
// ./webpack/js/pages/InterviewPage.js